import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, Receipt, Pencil, Trash2, Loader2, ImagePlus, Sparkles, FileImage, X, Calendar,
} from "lucide-react";
import { expenseNoteAPI } from "@/lib/api";
import { toast } from "sonner";
import { DateField } from "@/components/ui/date-field";

const CATEGORIES = ["Repas", "Déplacement", "Hébergement", "Fournitures", "Autre"];
const PAYMENT_METHODS = [
  { value: "cb", label: "Carte bancaire" },
  { value: "especes", label: "Espèces" },
  { value: "cheque", label: "Chèque" },
  { value: "virement", label: "Virement" },
  { value: "autre", label: "Autre" },
];

const CATEGORY_COLORS = {
  "Repas": "bg-amber-100 text-amber-800 border-amber-200",
  "Déplacement": "bg-sky-100 text-sky-800 border-sky-200",
  "Hébergement": "bg-violet-100 text-violet-800 border-violet-200",
  "Fournitures": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Autre": "bg-slate-100 text-slate-700 border-slate-200",
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtEUR = (n) => (Number(n) || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
const fmtDate = (s) => {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (!isNaN(d)) return d.toLocaleDateString("fr-FR");
  } catch { /* ignore */ }
  return s;
};

const emptyForm = {
  date: todayISO(),
  vendor: "",
  total_ttc: "",
  total_ht: "",
  tva_amount: "",
  tva_rate: 20,
  category: "Autre",
  payment_method: "cb",
  note: "",
  ticket_image_base64: "",
  ticket_mime: "",
};

function TicketDropZone({ previewDataUrl, onFile, onClear, scanning }) {
  const inputRef = useRef(null);
  const [dragover, setDragover] = useState(false);

  const handleFile = (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Format requis : JPEG, PNG ou WEBP");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image trop volumineuse (max 5 Mo)");
      return;
    }
    onFile(file);
  };

  if (previewDataUrl) {
    return (
      <div className="relative rounded-xl border border-slate-200 bg-slate-50 p-3 overflow-hidden" data-testid="ticket-preview">
        <img src={previewDataUrl} alt="Ticket" className="max-h-72 mx-auto object-contain rounded-md" />
        {!scanning && (
          <button
            type="button"
            onClick={onClear}
            className="absolute top-2 right-2 p-1.5 rounded-full bg-white/90 hover:bg-white text-slate-700 shadow-sm border border-slate-200"
            data-testid="ticket-clear-btn"
            aria-label="Supprimer le ticket"
          >
            <X size={14} />
          </button>
        )}
        {scanning && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex flex-col items-center justify-center gap-2">
            <Loader2 className="animate-spin text-indigo-600" size={28} />
            <p className="text-sm font-medium text-slate-700">Analyse OCR en cours…</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragover(true); }}
      onDragLeave={() => setDragover(false)}
      onDrop={(e) => { e.preventDefault(); setDragover(false); handleFile(e.dataTransfer.files?.[0]); }}
      onClick={() => inputRef.current?.click()}
      className={`cursor-pointer rounded-xl border-2 border-dashed transition-colors p-6 text-center ${dragover ? "border-indigo-500 bg-indigo-50/40" : "border-slate-300 bg-slate-50/40 hover:bg-slate-100/50"}`}
      data-testid="ticket-dropzone"
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid="ticket-file-input"
      />
      <div className="mx-auto w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 mb-3">
        <ImagePlus size={22} strokeWidth={1.5} />
      </div>
      <p className="text-sm font-medium text-slate-800">Glissez un ticket ici ou cliquez pour parcourir</p>
      <p className="text-xs text-slate-500 mt-1">L'OCR remplira automatiquement les champs (JPEG, PNG, WEBP – max 5 Mo)</p>
    </div>
  );
}

function ExpenseNoteDialog({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewDataUrl, setPreviewDataUrl] = useState("");

  useEffect(() => {
    if (!open) {
      setForm(emptyForm);
      setPreviewDataUrl("");
      setScanning(false);
      return;
    }
    if (editing) {
      setForm({
        date: editing.date || todayISO(),
        vendor: editing.vendor || "",
        total_ttc: editing.total_ttc ?? "",
        total_ht: editing.total_ht ?? "",
        tva_amount: editing.tva_amount ?? "",
        tva_rate: editing.tva_rate ?? 20,
        category: editing.category || "Autre",
        payment_method: editing.payment_method || "cb",
        note: editing.note || "",
        ticket_image_base64: "",
        ticket_mime: "",
      });
      // Lazy load preview if a ticket exists
      if (editing.has_ticket || editing.ticket_mime) {
        expenseNoteAPI.getTicket(editing.id)
          .then((r) => setPreviewDataUrl(r.data.data_url || ""))
          .catch(() => setPreviewDataUrl(""));
      } else {
        setPreviewDataUrl("");
      }
    } else {
      setForm(emptyForm);
      setPreviewDataUrl("");
    }
  }, [open, editing]);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleScan = async (file) => {
    // Local preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setPreviewDataUrl(e.target?.result || "");
    reader.readAsDataURL(file);

    setScanning(true);
    try {
      const res = await expenseNoteAPI.scan(file);
      const d = res.data || {};
      setForm((prev) => ({
        ...prev,
        date: d.date || prev.date,
        vendor: d.vendor || prev.vendor,
        total_ttc: d.total_ttc || prev.total_ttc,
        total_ht: d.total_ht || prev.total_ht,
        tva_amount: d.tva_amount || prev.tva_amount,
        tva_rate: d.tva_rate || prev.tva_rate,
        category: d.category || prev.category,
        payment_method: d.payment_method || prev.payment_method,
        note: d.note || prev.note,
        ticket_image_base64: d.ticket_image_base64 || "",
        ticket_mime: d.ticket_mime || file.type,
      }));
      toast.success("Ticket analysé, vérifiez les champs");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur OCR, saisissez manuellement");
    } finally {
      setScanning(false);
    }
  };

  const handleClearTicket = () => {
    setPreviewDataUrl("");
    setForm((prev) => ({ ...prev, ticket_image_base64: "", ticket_mime: "" }));
  };

  const handleSubmit = async () => {
    if (!form.date) { toast.error("Date requise"); return; }
    if (!form.vendor && !form.note) { toast.error("Indiquez au moins un marchand ou une note"); return; }
    setSaving(true);
    try {
      const payload = {
        date: form.date,
        vendor: form.vendor,
        total_ttc: Number(form.total_ttc) || 0,
        total_ht: Number(form.total_ht) || 0,
        tva_amount: Number(form.tva_amount) || 0,
        tva_rate: Number(form.tva_rate) || 0,
        category: form.category,
        payment_method: form.payment_method,
        note: form.note,
      };
      if (form.ticket_image_base64) {
        payload.ticket_image_base64 = form.ticket_image_base64;
        payload.ticket_mime = form.ticket_mime;
      }
      if (editing) {
        await expenseNoteAPI.update(editing.id, payload);
        toast.success("Note de frais mise à jour");
      } else {
        try {
          await expenseNoteAPI.create(payload);
          toast.success("Note de frais enregistrée");
        } catch (err) {
          // Détection de doublon : 409 avec detail.code = 'duplicate_expense_note'
          const detail = err?.response?.data?.detail;
          if (err?.response?.status === 409 && detail && typeof detail === "object" && detail.code === "duplicate_expense_note") {
            const ex = detail.existing || {};
            const msg = `Une note similaire existe déjà :\n- Date : ${ex.date}\n- Marchand : ${ex.vendor || "(sans marchand)"}\n- Montant TTC : ${ex.total_ttc} €\n- Catégorie : ${ex.category}\n\nVoulez-vous quand même créer un doublon ?`;
            if (!window.confirm(msg)) {
              setSaving(false);
              return;
            }
            await expenseNoteAPI.create(payload, true);
            toast.success("Note de frais enregistrée (doublon confirmé)");
          } else {
            throw err;
          }
        }
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="expense-note-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt size={18} />
            {editing ? "Modifier la note de frais" : "Nouvelle note de frais"}
          </DialogTitle>
          <DialogDescription>
            Scannez votre ticket pour pré-remplir automatiquement les champs, ou saisissez-les manuellement.
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-5">
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 uppercase tracking-wide">
              <Sparkles size={12} className="text-indigo-500" />
              Ticket scanné
            </Label>
            <TicketDropZone
              previewDataUrl={previewDataUrl}
              onFile={handleScan}
              onClear={handleClearTicket}
              scanning={scanning}
            />
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="exp-date">Date *</Label>
                <DateField id="exp-date" value={form.date} onChange={(e) => setField("date", e.target.value)} data-testid="exp-date-input" />
              </div>
              <div>
                <Label htmlFor="exp-category">Catégorie</Label>
                <Select value={form.category} onValueChange={(v) => setField("category", v)}>
                  <SelectTrigger id="exp-category" data-testid="exp-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="exp-vendor">Marchand</Label>
              <Input id="exp-vendor" value={form.vendor} onChange={(e) => setField("vendor", e.target.value)} placeholder="Ex. Restaurant Le Provençal" data-testid="exp-vendor-input" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="exp-ttc">Total TTC (€)</Label>
                <Input id="exp-ttc" type="number" step="0.01" value={form.total_ttc} onChange={(e) => setField("total_ttc", e.target.value)} data-testid="exp-ttc-input" />
              </div>
              <div>
                <Label htmlFor="exp-tva-rate">Taux TVA (%)</Label>
                <Input id="exp-tva-rate" type="number" step="0.5" value={form.tva_rate} onChange={(e) => setField("tva_rate", e.target.value)} data-testid="exp-tva-rate-input" />
              </div>
              <div>
                <Label htmlFor="exp-tva-amount">TVA (€)</Label>
                <Input id="exp-tva-amount" type="number" step="0.01" value={form.tva_amount} onChange={(e) => setField("tva_amount", e.target.value)} data-testid="exp-tva-amount-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="exp-ht">Total HT (€)</Label>
                <Input id="exp-ht" type="number" step="0.01" value={form.total_ht} onChange={(e) => setField("total_ht", e.target.value)} data-testid="exp-ht-input" />
              </div>
              <div>
                <Label htmlFor="exp-payment">Mode de paiement</Label>
                <Select value={form.payment_method} onValueChange={(v) => setField("payment_method", v)}>
                  <SelectTrigger id="exp-payment" data-testid="exp-payment-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="exp-note">Note</Label>
              <Textarea id="exp-note" rows={3} value={form.note} onChange={(e) => setField("note", e.target.value)} placeholder="Ex. Déjeuner client X, rendez-vous prospection…" data-testid="exp-note-input" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="exp-cancel-btn">Annuler</Button>
          <Button onClick={handleSubmit} disabled={saving || scanning} data-testid="exp-save-btn">
            {saving && <Loader2 className="animate-spin mr-2" size={14} />}
            {editing ? "Mettre à jour" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TicketViewerDialog({ noteId, open, onOpenChange }) {
  const [dataUrl, setDataUrl] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !noteId) { setDataUrl(""); return; }
    setLoading(true);
    expenseNoteAPI.getTicket(noteId)
      .then((r) => setDataUrl(r.data.data_url || ""))
      .catch(() => toast.error("Ticket introuvable"))
      .finally(() => setLoading(false));
  }, [open, noteId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="ticket-viewer-dialog">
        <DialogHeader>
          <DialogTitle>Ticket</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-indigo-600" /></div>
        ) : dataUrl ? (
          <img src={dataUrl} alt="Ticket" className="w-full max-h-[70vh] object-contain rounded-md" />
        ) : (
          <p className="text-sm text-slate-500 py-6 text-center">Aucune image disponible.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ExpenseNotesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewerNoteId, setViewerNoteId] = useState(null);

  // Ouverture automatique du dialogue via ?new=1 (menu global QuickCreate + sidebar)
  useEffect(() => {
    if (searchParams.get("new")) {
      setEditing(null);
      setDialogOpen(true);
      // Nettoyer le param pour eviter la re-ouverture apres fermeture
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory && filterCategory !== "all") params.category = filterCategory;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await expenseNoteAPI.list(params);
      setNotes(res.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [filterCategory, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (note) => {
    if (!window.confirm(`Supprimer la note "${note.vendor || note.note || note.id}" ?`)) return;
    try {
      await expenseNoteAPI.delete(note.id);
      toast.success("Note supprimée");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur suppression");
    }
  };

  const totalTTC = notes.reduce((s, n) => s + (Number(n.total_ttc) || 0), 0);
  const totalTVA = notes.reduce((s, n) => s + (Number(n.tva_amount) || 0), 0);

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto" data-testid="expense-notes-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Receipt size={24} className="text-indigo-600" />
          Notes de frais
        </h1>
        <p className="text-sm text-slate-500 mt-1">Scannez vos tickets, l'IA pré-remplit le reste.</p>
        <Button
          onClick={() => { setEditing(null); setDialogOpen(true); }}
          className="mt-3 bg-indigo-600 hover:bg-indigo-700 text-white"
          data-testid="add-expense-note-btn"
        >
          <Plus size={16} className="mr-2" /> Nouvelle note
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Card className="p-3">
          <p className="text-[11px] text-slate-500 uppercase tracking-wide">Total TTC</p>
          <p className="text-lg font-semibold text-slate-900 mt-0.5" data-testid="stat-total-ttc">{fmtEUR(totalTTC)}</p>
        </Card>
        <Card className="p-3">
          <p className="text-[11px] text-slate-500 uppercase tracking-wide">TVA récupérable</p>
          <p className="text-lg font-semibold text-emerald-700 mt-0.5" data-testid="stat-total-tva">{fmtEUR(totalTVA)}</p>
        </Card>
        <Card className="p-3">
          <p className="text-[11px] text-slate-500 uppercase tracking-wide">Nb de tickets</p>
          <p className="text-lg font-semibold text-slate-900 mt-0.5" data-testid="stat-count">{notes.length}</p>
        </Card>
        <Card className="p-3">
          <p className="text-[11px] text-slate-500 uppercase tracking-wide">Avec image</p>
          <p className="text-lg font-semibold text-slate-900 mt-0.5">{notes.filter((n) => n.has_ticket).length}</p>
        </Card>
      </div>

      <Card className="p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[140px]">
            <Label className="text-xs">Catégorie</Label>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger data-testid="filter-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Du</Label>
            <DateField value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" />
          </div>
          <div>
            <Label className="text-xs">Au</Label>
            <DateField value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" />
          </div>
          {(filterCategory !== "all" || dateFrom || dateTo) && (
            <Button variant="ghost" size="sm" onClick={() => { setFilterCategory("all"); setDateFrom(""); setDateTo(""); }}>
              Réinitialiser
            </Button>
          )}
        </div>
      </Card>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-600" /></div>
        ) : notes.length === 0 ? (
          <div className="py-16 text-center text-slate-500">
            <Receipt size={32} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm">Aucune note de frais pour le moment.</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => { setEditing(null); setDialogOpen(true); }}>
              <Plus size={14} className="mr-1.5" /> Créer la première
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Marchand</TableHead>
                <TableHead>Catégorie</TableHead>
                <TableHead className="text-right">HT</TableHead>
                <TableHead className="text-right">TVA</TableHead>
                <TableHead className="text-right">TTC</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Auteur</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {notes.map((n) => (
                <TableRow key={n.id} data-testid={`expense-row-${n.id}`}>
                  <TableCell className="whitespace-nowrap text-sm">
                    <Calendar size={12} className="inline mr-1 text-slate-400" />
                    {fmtDate(n.date)}
                  </TableCell>
                  <TableCell className="font-medium text-slate-900">
                    {n.vendor || <span className="text-slate-400 italic">—</span>}
                    {n.note && <p className="text-xs text-slate-500 font-normal mt-0.5 line-clamp-1">{n.note}</p>}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={CATEGORY_COLORS[n.category] || CATEGORY_COLORS["Autre"]}>
                      {n.category || "Autre"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm">{fmtEUR(n.total_ht)}</TableCell>
                  <TableCell className="text-right tabular-nums text-sm text-emerald-700">{fmtEUR(n.tva_amount)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{fmtEUR(n.total_ttc)}</TableCell>
                  <TableCell className="text-xs text-slate-600 uppercase">{n.payment_method || "—"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{n.created_by_name || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {n.has_ticket && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setViewerNoteId(n.id)} data-testid={`view-ticket-${n.id}`} title="Voir le ticket">
                          <FileImage size={15} />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditing(n); setDialogOpen(true); }} data-testid={`edit-expense-${n.id}`} title="Modifier">
                        <Pencil size={15} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(n)} data-testid={`delete-expense-${n.id}`} title="Supprimer">
                        <Trash2 size={15} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <ExpenseNoteDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        onSaved={load}
      />
      <TicketViewerDialog
        noteId={viewerNoteId}
        open={!!viewerNoteId}
        onOpenChange={(v) => { if (!v) setViewerNoteId(null); }}
      />
    </div>
  );
}
